import { Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  HttpErrorResponse,
  HttpEventType,
  HttpResponse,
} from '@angular/common/http';
import {
  Component,
  EventEmitter,
  IterableDiffers,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { IFileInfo } from '@common/interfaces';
import * as FileSaver from 'file-saver';
import {
  PROGRESS_STATUS,
  UPLOAD_LIST_TYPES,
} from 'libs/common/src/lib/constants/constants';
import { CommonHttpService } from 'libs/common/src/lib/service/common-http.service';
import { UploadDownloadService } from 'libs/common/src/lib/service/upload-download.service';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import * as XLSX from 'xlsx';
import { GenericConfirmationPopUpComponent } from '../generic-confirmation-pop-up/generic-confirmation-pop-up.component';
import { GenericPopUpComponent } from '../generic-pop-up/generic-pop-up.component';

@Component({
  selector: 'app-bulk-upload-list',
  templateUrl: './bulk-upload-list.component.html',
  styleUrls: ['./bulk-upload-list.component.scss'],
})
export class BulkUploadListComponent implements OnInit {
  @Output() closeModal = new EventEmitter();

  popupForm = new UntypedFormGroup({
    upload_list_type: new UntypedFormControl('', Validators.required),
    files: new UntypedFormControl('', Validators.required),
  });

  iterableDiffer: any;
  options = UPLOAD_LIST_TYPES;
  loaderView!: boolean;
  loading = false;
  progressInfos: any[] = [];
  response!: boolean;
  fileInfo!: IFileInfo;
  uploadInProgress = false;
  uploadCount!: any;
  uploadListTypeHolder!: string;
  fileTypes = ['text/csv'];

  constructor(
    private commonHttpService: CommonHttpService,
    private iterableDiffers: IterableDiffers,
    private dialogRef: DialogRef,
    private dialog: Dialog,
    private uploadAndDownloadService: UploadDownloadService,
    private toasterService: ToastrService
  ) {
    this.iterableDiffer = this.iterableDiffers.find([]).create();
  }

  ngOnInit(): void { }

  close(uploadCount?: any) {
    this.progressInfos = [];
    this.popupForm.reset();
    const current = this;
    const hasErrors =
      uploadCount && uploadCount.some((count: any) => count.status === 'fail');

    if (!hasErrors) {
      this.dialogRef.removePanelClass('download-upload-dialog');
      this.dialogRef.addPanelClass('close-upload-download-dialog');
      setTimeout(() => {
        current.dialogRef.close(uploadCount);
      }, 1000);
    }
  }

  closeDialog() {
    this.openCloseConfirmationDialog();
  }
  getValue(value: string) {
    this.uploadListTypeHolder = value;
    this.popupForm.get('upload_list_type')?.setValue(value);
  }

  onSubmit() {
    this.openConfirmationDialog();
  }

  upload(file: File, fileType: string): Observable<void> {
    const completionSubject = new Subject<void>;
    if (file && fileType === 'asset_list') {
      this.uploadInProgress = true;
      this.fileInfo.showRemoveFileBtn = false;
      this.fileInfo.showProgressBar = true;

      let formData = new FormData();
      formData.append('uploadedFile', file);
      this.commonHttpService.bulkAssetListUpload(formData).subscribe({
        next: (res: any) => {
          if (res.type === HttpEventType.UploadProgress && file) {
            this.fileInfo.progressValue = Math.round(
              (100 * res.loaded) / res.total
            );
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
          } else if (res instanceof HttpResponse) {
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
            this.fileInfo.showRemoveFileBtn = true;
            const dataLength = res.body.dataNotInserted.length
            const uploadCounts = {
              status: 'success',
              fileName: file.name,
              successCount: res.body.successfulUploads.createdAssets.length,
              totalCount: res.body.totalCount,
              failedCount: res.body.unsuccessfulUploads,
              ...(dataLength > 0 && {
                failedReason: this.formatErrorMessage(res.body.dataNotInserted[dataLength - 1].errorReason)
              })
            };

            this.openSuccessBulkUploadModal(uploadCounts);
            if (res.body.dataNotInserted?.length > 0) {
              this.generateExcelFileFromFailedRecords(res.body.dataNotInserted, 'Failed_Asset_Uploads');
            }
            if (res.body.data?.length)
              this.saveDataInCSV('Asset List', res.body.data);

            completionSubject.next();
            completionSubject.complete();
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorReason = Object(error).error.error.errorList;

          this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
          this.fileInfo.showProgressBar = false;
          this.fileInfo.showRemoveFileBtn = true;
          this.openFailedBulkModal(error);
          this.uploadInProgress = false;

          completionSubject.next();
          completionSubject.complete();
        },
      });
    } else if (file && fileType === 'asset_allocation_list') {
      this.uploadInProgress = true;
      this.fileInfo.showRemoveFileBtn = false;
      this.fileInfo.showProgressBar = true;
      let formData = new FormData();
      formData.append('uploadedFile', file);
      this.commonHttpService.bulkAssetAllocationListUpload(formData).subscribe({
        next: (res: any) => {
          if (res.type === HttpEventType.UploadProgress && file) {
            this.fileInfo.progressValue = Math.round(
              (100 * res.loaded) / res.total
            );
          } else if (res instanceof HttpResponse) {
            this.fileInfo.showRemoveFileBtn = true;
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
            const uploadCounts = {
              status: 'success',
              fileName: file.name,
              successCount: res.body.successfulUploads,
              totalCount: res.body.totalCount,
              failedCount: res.body.unsuccessfulUploads,
            };

            this.openSuccessBulkUploadModal(uploadCounts);

            if (res.body.data?.length)
              this.saveDataInCSV('Asset Allocation List', res.body.data);
            completionSubject.next();
            completionSubject.complete();
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorReason = Object(error).error.error.errorList;
          this.loaderView = false;
          this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
          this.fileInfo.showProgressBar = false;
          this.fileInfo.showRemoveFileBtn = true;
          // this.openFailedBulkModal(error);
          this.toasterService.error(error?.error?.error?.errorList, '', {
            toastClass: 'toaster-wrapper',
          });
          this.uploadInProgress = false;
          completionSubject.next();
          completionSubject.complete()
        },
      });
    } else if (file && fileType === 'asset_list_update') {
      this.uploadInProgress = true;
      this.fileInfo.showRemoveFileBtn = false;
      this.fileInfo.showProgressBar = true;
      let formData = new FormData();
      formData.append('uploadedFile', file);
      this.commonHttpService.bulkAssetListUpdateUpload(formData).subscribe({
        next: (res: any) => {
          if (res.type === HttpEventType.UploadProgress && file) {
            this.fileInfo.progressValue = Math.round(
              (100 * res.loaded) / res.total
            );
          } else if (res instanceof HttpResponse) {
            this.fileInfo.showRemoveFileBtn = true;
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
            const uploadCounts = {
              status: 'success',
              fileName: file.name,
              successCount: res.body.data.successfulUploads,
              totalCount: res.body.data.totalCount,
              failedCount: res.body.data.unsuccessfulUploads,
            };

            this.openSuccessBulkUploadModal(uploadCounts);
            if (res.body.data?.notUpdatedAssets?.length > 0) {
              this.generateExcelFileFromFailedRecords(res.body.data.notUpdatedAssets, 'Failed_Asset_Uploads');
            }
            if (res.body.data?.length)
              this.saveDataInCSV('Asset List Update', res.body.data);
            completionSubject.next();
            completionSubject.complete()
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorReason = Object(error).error.error.errorList;
          this.loaderView = false;
          this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
          this.fileInfo.showProgressBar = false;
          this.fileInfo.showRemoveFileBtn = true;
          this.openFailedBulkModal(error);
          this.uploadInProgress = false;
          completionSubject.next();
          completionSubject.complete()
        },
      });
    } else if (file && fileType === 'asset_out_for_repair') {
      this.uploadInProgress = true;
      this.fileInfo.showRemoveFileBtn = false;
      this.fileInfo.showProgressBar = true;
      let formData = new FormData();
      formData.append('uploadedFile', file);
      this.commonHttpService.bulkAssetOutForRepairUpload(formData).subscribe({
        next: (res: any) => {
          if (res.type === HttpEventType.UploadProgress && file) {
            this.fileInfo.progressValue = Math.round(
              (100 * res.loaded) / res.total
            );
          } else if (res instanceof HttpResponse) {
            this.fileInfo.showRemoveFileBtn = true;
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
            const uploadCounts = {
              status: 'success',
              fileName: file.name,
              successCount: res.body.successfulUploads,
              totalCount: res.body.totalCount,
              failedCount: res.body.unsuccessfulUploads,
            };

            this.openSuccessBulkUploadModal(uploadCounts);

            if (res.body.data?.length)
              this.saveDataInCSV('Asset Out for Repair List', res.body.data);
            completionSubject.next();
            completionSubject.complete()
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorReason = Object(error).error.error.errorList;
          this.loaderView = false;
          this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
          this.fileInfo.showProgressBar = false;
          this.fileInfo.showRemoveFileBtn = true;
          // this.openFailedBulkModal(error);
          this.toasterService.error(errorReason, '', {
            toastClass: 'toaster-wrapper',
          });
          this.uploadInProgress = false;
          completionSubject.next();
          completionSubject.complete()
        },
      });
    } else if (file && fileType === 'mark_asset_ewaste') {
      this.fileInfo.showRemoveFileBtn = false;
      this.fileInfo.showProgressBar = true;
      let formData = new FormData();
      formData.append('uploadedFile', file);
      this.uploadInProgress = true;
      this.commonHttpService.uploadMarkAssetAsEwaste(formData).subscribe({
        next: (res: any) => {
          if (res.type === HttpEventType.UploadProgress && file) {
            this.fileInfo.progressValue = Math.round(
              (100 * res.loaded) / res.total
            );
          } else if (res instanceof HttpResponse) {
            this.fileInfo.showRemoveFileBtn = true;
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
            const uploadCounts = {
              status: 'success',
              fileName: file.name,
              successCount: res.body.successfulUploads,
              totalCount: res.body.totalCount,
              failedCount: res.body.unsuccessfulUploads,
            };
            this.openSuccessBulkUploadModal(uploadCounts);

            if (res.body.data?.length) {
              this.saveDataInCSV('Assets Marked as E-Waste', res.body.data);
            }
            completionSubject.next();
            completionSubject.complete()
          }
        },
        error: (error: HttpErrorResponse) => {
          const errorReason = Object(error).error.error.errorList;
          this.loaderView = false;
          this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
          this.fileInfo.showProgressBar = false;
          this.fileInfo.showRemoveFileBtn = true;
          // this.openFailedBulkModal(error);
          this.toasterService.error(error?.error?.error?.errorList, '', {
            toastClass: 'toaster-wrapper',
          });
          this.uploadInProgress = false;
          completionSubject.next();
          completionSubject.complete()
        },
      });
    } else if (file && fileType === 'user_list') {
      this.uploadInProgress = true;
      this.fileInfo.showRemoveFileBtn = false;
      this.fileInfo.showProgressBar = true;

      let formData = new FormData();
      formData.append('uploadedFile', file);

      this.commonHttpService.bulkUserListUpload(formData).subscribe({
        next: (res: any) => {
          if (res.type === HttpEventType.UploadProgress && file) {
            this.fileInfo.progressValue = Math.round((100 * res.loaded) / res.total);
          } else if (res instanceof HttpResponse) {
            this.fileInfo.showRemoveFileBtn = true;
            this.fileInfo.progressStatus = PROGRESS_STATUS.SUCCESS;
            const uploadCounts = {
              status: 'success',
              fileName: file.name,
              successCount: res.body.data.successfulUploads.insertedUsers.length,
              totalCount: res.body.data.totalCount,
              failedCount: res.body.data.unsuccessfulUploads,
              errorMsg: res?.body?.data?.dataNotInserted?.[0]?.errorReason ||
                res?.body?.data?.dataNotInserted?.[0]?.reason ||
                ''
            };

            this.openSuccessBulkUploadModal(uploadCounts);
            if (res.body.data?.dataNotInserted?.length > 0) {
              this.generateExcelFileFromFailedRecords(res.body.data.dataNotInserted, 'Failed_User_Uploads');
            }
            if (res.body.data?.length) {
              this.saveDataInCSV('User List', res.body.data);
            }
            completionSubject.next();
            completionSubject.complete()
          }
        },
        error: (error: HttpErrorResponse) => {
          this.loaderView = false;
          this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
          this.fileInfo.showProgressBar = false;
          this.fileInfo.showRemoveFileBtn = true;
          this.toasterService.error(error?.error?.error?.errorList, '', {
            toastClass: 'toaster-wrapper',
          });
          this.uploadInProgress = false;
          completionSubject.next();
          completionSubject.complete()
        },
      });
    } else {
      this.fileInfo.progressStatus = PROGRESS_STATUS.FAILED;
      this.fileInfo.showProgressBar = false;
      this.fileInfo.showRemoveFileBtn = true;
      this.uploadInProgress = false;
      completionSubject.next();
      completionSubject.complete()
    }
    return completionSubject.asObservable();
  }
  ngDoCheck() { }

  selectedFiles(files: any) {
    if (files[0] && files[0].file.progressStatus !== PROGRESS_STATUS.FAILED) {
      this.fileInfo = files[0] as IFileInfo;
      this.fileInfo.showProgressBar = false;
      this.popupForm.patchValue({
        upload_list_type: this.uploadListTypeHolder,
        files: this.fileInfo.file,
      });
    }
  }
  saveDataInCSV(name: string, data: Array<any>) {
    let csvContent = this.uploadAndDownloadService.saveDataInCSV(data);
    const exportCSV = document.createElement('a');
    exportCSV.href = 'data:text/csv;charset=utf-8,' + encodeURI(csvContent);
    exportCSV.target = '_blank';
    exportCSV.download = name + '.csv';
    exportCSV.click();
  }

  openConfirmationDialog() {
    const dialogRef = this.dialog.open<string>(
      GenericConfirmationPopUpComponent,
      {
        panelClass: 'center-dialog-box',
        backdropClass: 'backdrop',
        disableClose: true,
        data: {
          header: 'Do you want to upload the file?',
          description: 'This action cannot be undone',
          primaryBtn: 'Upload File',
          secondaryBtn: 'Cancel',
          image: 'cautionImage',
        },
      }
    );
    dialogRef.closed.subscribe((response: any) => {
      if (response.buttonText === 'Upload File') {
        this.response = true;
        const selectedFiles = this.popupForm.value.files;
        const fileType = this.popupForm.value.upload_list_type;
        this.progressInfos = [];

        if (selectedFiles) {
          this.loaderView = true;
          this.fileInfo.showRemoveFileBtn = true;
          this.fileInfo.showProgressBar = true;
          this.fileInfo.progressValue = 0;
          this.upload(selectedFiles, fileType).subscribe(() => {
            this.loaderView = false;
            this.uploadAndDownloadService.closeModal()
          })
        }
      }
    });
  }
  generateExcelFileFromFailedRecords(data: any[], fileName: string): void {
    if (!data || data.length === 0) return;

    const flattened = data.map((item) => {
      const properties = item.properties?.reduce((acc: any, prop: any) => {
        acc[prop.displayName || prop.name] = prop.value;
        return acc;
      }, {}) || {};

      return {
        'Sr No': item['Sr No'],
        Acronym: item.Acronym,
        'Asset Code': item['Asset Code'],
        Category: item.Category,
        Model: item.Model,
        'Purchase Date': item['Purchase Date'],
        'Vendor Name': item['Vendor Name'],
        'Owned By': item['Owned By'],
        'Managed By': item['Managed By'],
        'Invoice Number': item['Invoice Number'],
        Location: item.Location,
        'Purchase Cost': item['Purchase Cost'],
        Quality: item.Quality,
        'Quality Unit': item['Quality Unit'],
        'Screen Size': item['Screen Size'],
        Warranty: item.Warranty,
        'Warranty Unit': item['Warranty Unit'],
        'Error Reason': item.errorReason,
        ...properties // Append dynamic properties at the end
      };
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(flattened);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Failed Uploads': worksheet },
      SheetNames: ['Failed Uploads'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    FileSaver.saveAs(blob, fileName + '.xlsx');
  }

  openCloseConfirmationDialog() {
    const dialogRef = this.dialog.open<string>(
      GenericConfirmationPopUpComponent,
      {
        panelClass: 'center-dialog-box',
        backdropClass: 'backdrop',
        disableClose: true,
        data: {
          header: 'Are you sure you want to cancel?',
          description: 'Warning: This action will permanently discard all unsaved changes',
          primaryBtn: 'Discard',
          secondaryBtn: 'Go Back',
          image: 'cautionImage',
        },
      }
    );
    dialogRef.closed.subscribe((response) => {
      if (response === 'Discard') {
        this.progressInfos = [];
        this.popupForm.reset();
        this.dialogRef.removePanelClass('download-upload-dialog');
        this.dialogRef.addPanelClass('close-upload-download-dialog');
        const current = this;
        setTimeout(() => {
          current.dialogRef.close();
        }, 1000);
      }
    });
  }

  openSuccessBulkUploadModal(res: any) {
    const popUpConfig = {
      headerIcon: 'success',
      title: 'Uploaded Documents Summary',
      subtitle: 'Selected documents have been uploaded successfully',
      data: [{ ...res }],
      hasSummary: true,
    };
    const dialogRef = this.dialog.open<string>(GenericPopUpComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      data: popUpConfig,
      width: '50%',
    });
    dialogRef.closed.subscribe((response) => {
      this.close();
    });
  }

  refineError(errorObj: any) {
    const colsArr = errorObj.map((err: any) => err.column);
    return colsArr.join(', ')
  }

  openFailedBulkModal(error: HttpErrorResponse) {
    const errorList = error?.error?.error?.errorList ?? [];
    const missingColumns = this.extractUniqueColumns(errorList, 'Missing column:');
    const invalidColumns = this.extractUniqueColumns(errorList, 'Invalid column:');
    let subtitle = '';
    if (missingColumns.length) {
      subtitle += 'Missing Columns:\n' + this.columnsToGrid(missingColumns) + '\n\n';
    }
    if (invalidColumns.length) {
      subtitle += 'Invalid Columns:\n' + this.columnsToGrid(invalidColumns) + '\n\n';
    }
    if (!subtitle) {
      subtitle = 'Looks like you lost your connection. Please check your connection and try again.';
    }
    const popUpConfig = {
      headerIcon: 'error',
      title: 'Uploaded Documents Summary',
      subtitle: subtitle.trim(),
      hasSummary: true,
    };
    const dialogRef = this.dialog.open<string>(GenericPopUpComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      data: popUpConfig,
      width: '60%'
    });
    dialogRef.closed.subscribe((response) => { });
  }
  extractUniqueColumns(errorList: any[], type: string): string[] {
    const seen = new Set<string>();

    return errorList
      .filter(err => err.msg.startsWith(type))
      .map(err => err.column)
      .filter(column => {
        if (!seen.has(column)) {
          seen.add(column);
          return true;
        }
        return false;
      });
  }
  private columnsToGrid(columns: string[], columnsPerRow: number = 4): string {
    let grid = '';
    for (let i = 0; i < columns.length; i++) {
      grid += columns[i] + '   ';
      if ((i + 1) % columnsPerRow === 0) {
        grid += '\n';
      }
    }
    return grid.trim();
  }
  formatErrorMessage = (raw: string): string => {
    const parts = raw.split('|').map(p => p.trim());
    const cols = parts
      .filter(p => p.startsWith('Missing required value for column:'))
      .map(p => p.replace('Missing required value for column:', '').trim());
    const others = parts.filter(p => !p.startsWith('Missing required value for column:'));

    return [
      cols.length ? `Missing required value for columns: ${cols.join(', ')}` : '',
      ...others
    ].filter(Boolean).join('. ');
  }
}

